import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CircleCheck, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLang } from "@/lib/i18n";

export const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.2, 0.8, 0.2, 1] } },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

export function Reveal({ children, className = "", ...props }) {
  return (
    <motion.div
      className={className}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/* Light-first section heading. Pass dark for dark sections. */
export function SectionHeading({ eyebrow, title, subtitle, center = false, dark = false }) {
  return (
    <Reveal className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
      {eyebrow && (
        <div className={`flex items-center gap-3 ${center ? "justify-center" : ""}`}>
          <span className="h-px w-8 bg-[#FF3C20]" />
          <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${dark ? "text-white/60" : "text-[#111318]/60"}`}>
            {eyebrow}
          </p>
        </div>
      )}
      <h2 className={`mt-4 font-display text-3xl sm:text-4xl leading-[1.08] font-semibold tracking-tight ${dark ? "text-white" : "text-[#111318]"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base lg:text-lg leading-relaxed ${dark ? "text-white/60" : "text-[#3B4452]"}`}>{subtitle}</p>
      )}
    </Reveal>
  );
}

export function FestgeldStatusBadge({ status, size = "default", dark = false }) {
  const { t } = useLang();
  const light = {
    in_pruefung: { label: t("Aktuell in Pr\u00fcfung", "Currently under review"), cls: "bg-[#ECEEF1] text-[#3B4452] border-[#C9CED6]/60" },
    begrenzt: { label: t("Begrenzte Verf\u00fcgbarkeit", "Limited availability"), cls: "bg-[#FDEFEC] text-[#E8241B] border-[#FF3C20]/50" },
    keine_anzeige: { label: t("Keine \u00f6ffentliche Konditionsanzeige", "No public rate disclosure"), cls: "bg-[#ECEEF1] text-[#3B4452] border-[#D7DADF]" },
  };
  const darkCfg = {
    in_pruefung: { label: light.in_pruefung.label, cls: "bg-[#C9CED6]/10 text-[#C9CED6] border-[#C9CED6]/35" },
    begrenzt: { label: light.begrenzt.label, cls: "bg-[#FF3C20]/10 text-[#FF3C20] border-[#FF3C20]/40" },
    keine_anzeige: { label: light.keine_anzeige.label, cls: "bg-white/5 text-white/60 border-white/20" },
  };
  const set = dark ? darkCfg : light;
  const c = set[status] || set.in_pruefung;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`rounded-[6px] ${c.cls} ${size === "lg" ? "px-4 py-1.5 text-sm" : ""} gap-2 font-medium cursor-help`} data-testid="festgeld-status-badge">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-40" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current" />
            </span>
            {c.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {t(
            "Festgeldkonditionen werden ausschlie\u00dflich nach individueller Eignungs- und Verf\u00fcgbarkeitspr\u00fcfung kommuniziert. Eine Anfrage stellt kein Angebot dar.",
            "Fixed-term deposit conditions are communicated exclusively after individual suitability and availability checks. An inquiry does not constitute an offer."
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* Customer-facing trust band (no internal system names). */
export function TrustBar({ dark = false }) {
  const { t } = useLang();
  const items = [
    t("Pers\u00f6nliche Beratung", "Personal advice"),
    t("Transparente Pr\u00fcfung", "Transparent review"),
    t("Dokumentierte Prozesse", "Documented processes"),
    t("Digitale Anfragewege", "Digital request channels"),
    t("Langfristige Begleitung", "Long-term support"),
    t("DSGVO-konform", "GDPR-compliant"),
  ];
  return (
    <div className={dark ? "border-y border-white/[0.06] bg-white/[0.015]" : "border-y border-border bg-white"} data-testid="trust-bar">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        {items.map((item, i) => (
          <div key={item} className={`flex items-center justify-center gap-2 py-4 px-3 text-center ${i > 0 ? `lg:border-l ${dark ? "lg:border-white/[0.06]" : "lg:border-border"}` : ""}`}>
            <span className="h-1 w-1 rounded-full bg-[#FF3C20] shrink-0" />
            <span className={`text-[11px] sm:text-xs font-medium uppercase tracking-[0.1em] ${dark ? "text-white/55" : "text-[#6B7280]"}`}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProcessTimeline({ steps, dark = false }) {
  const cols = steps.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4";
  return (
    <motion.ol variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-5`} data-testid="process-timeline">
      {steps.map((step, i) => (
        <motion.li
          key={i}
          variants={fadeUp}
          className={dark ? "pt-6 border-t border-white/15 hover:border-[#FF3C20]/60 transition-colors duration-200" : "pt-6 border-t border-[#111318]/20 hover:border-[#FF3C20] transition-colors duration-200"}
        >
          <span className={`font-mono-num text-xs font-semibold ${dark ? "text-[#FF3C20]" : "text-[#E8241B]"}`}>0{i + 1}</span>
          <h4 className={`mt-4 text-base font-semibold ${dark ? "text-white" : "text-[#111318]"}`}>{step.title}</h4>
          <p className={`mt-2.5 text-sm leading-relaxed ${dark ? "text-white/55" : "text-[#3B4452]"}`}>{step.text}</p>
        </motion.li>
      ))}
    </motion.ol>
  );
}

export function FAQSection({ items, dark = false }) {
  return (
    <Accordion type="single" collapsible className="w-full" data-testid="faq-section">
      {items.map((item, i) => (
        <AccordionItem key={i} value={`item-${i}`} className={dark ? "border-white/10" : "border-border"}>
          <AccordionTrigger className={`text-left text-sm sm:text-base font-medium hover:no-underline ${dark ? "text-white" : "text-[#111318]"}`}>
            {item.q}
          </AccordionTrigger>
          <AccordionContent className={`text-sm leading-relaxed ${dark ? "text-white/55" : "text-[#3B4452]"}`}>
            {item.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

/* Final dark CTA \u2013 stays dark by design. */
export function CTASection({ title, subtitle, primaryLabel, primaryTo, secondaryLabel, secondaryTo }) {
  return (
    <section className="bg-[#111318] accent-hairline noise-overlay">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.08] font-semibold tracking-tight text-white">{title}</h2>
          <p className="mt-5 text-base lg:text-lg text-white/60 leading-relaxed">{subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-[6px] h-12 px-7 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold transition-colors duration-200" data-testid="cta-primary">
              <Link to={primaryTo}>
                {primaryLabel} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </Button>
            {secondaryLabel && (
              <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-7 border-white/30 text-white bg-transparent hover:bg-white/5 hover:border-white hover:text-white transition-colors duration-200" data-testid="cta-secondary">
                <Link to={secondaryTo}>{secondaryLabel}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ComplianceNote({ children, dark = false }) {
  return (
    <div
      className={`flex items-start gap-3 rounded-[8px] border p-4 text-xs leading-relaxed ${
        dark ? "border-white/10 bg-white/[0.025] text-white/50" : "border-border bg-[#F1F2F4] text-[#3B4452]"
      }`}
      data-testid="compliance-note"
    >
      <Info className={`h-4 w-4 shrink-0 mt-0.5 ${dark ? "text-[#FF3C20]/70" : "text-[#E8241B]"}`} />
      <span>{children}</span>
    </div>
  );
}

export function CheckItem({ children, dark = false }) {
  return (
    <span className={`flex items-start gap-2.5 text-sm leading-relaxed ${dark ? "text-white/65" : "text-[#2A303A]"}`}>
      <CircleCheck className={`h-4 w-4 shrink-0 mt-0.5 ${dark ? "text-[#FF3C20]" : "text-[#E8241B]"}`} />
      {children}
    </span>
  );
}
